export function calculatePace(distanceMiles: number, elapsedSeconds: number): number {
  if (distanceMiles <= 0 || elapsedSeconds <= 0) return 0
  const hours = elapsedSeconds / 3600
  const mph = distanceMiles / hours
  return 60 / mph
}

export function calculateAveragePace(totalDistanceMiles: number, totalElapsedSeconds: number): number {
  return calculatePace(totalDistanceMiles, totalElapsedSeconds)
}

export function calculateECT(
  currentPaceMinPerMile: number,
  currentRunnerRemaining: number,
  allRemaining: number,
  avgPaceMinPerMile: number
): number {
  if (currentRunnerRemaining <= 0) {
    return (allRemaining - currentRunnerRemaining) * avgPaceMinPerMile
  }
  const currentTime = currentRunnerRemaining * currentPaceMinPerMile
  const otherTime = (allRemaining - currentRunnerRemaining) * avgPaceMinPerMile
  return currentTime + otherTime
}

/**
 * Compute a pace multiplier for a leg based on its elevation profile and distance.
 *
 * Model:
 *   - Grade % = elevation change (ft) / (distance (mi) × 5280) × 100
 *     (treating gain and loss separately over the full leg distance)
 *   - Each 1% of avg uphill grade → ~4% slower pace
 *   - Each 1% of avg downhill grade → ~2% faster pace, capped at 12%
 *     (very steep descents don't speed runners up linearly)
 *   - Distance fatigue: each mile beyond 4.0 mi → +1% slower
 *     (runners stay fresh on short legs; longer legs accumulate fatigue)
 *   - Result clamped to [0.80, 1.40] to prevent wild outliers
 *
 * Falls back to the categorical difficulty rating when elevation data is absent.
 */
export function computeLegPaceMultiplier(
  distanceMiles: number | null | undefined,
  elevationGainFt: number | null | undefined,
  elevationLossFt: number | null | undefined,
  difficultyRating: string | null | undefined,
): number {
  const dist = distanceMiles ?? 0
  const gain = elevationGainFt ?? 0
  const loss = elevationLossFt ?? 0

  // Distance fatigue: each mile beyond 4.0 mi adds 1% slowdown
  const fatiguePenalty = Math.max(0, (dist - 4.0) * 0.01)

  if (dist > 0 && (gain > 0 || loss > 0)) {
    const gainGrade = (gain / (dist * 5280)) * 100
    const lossGrade = (loss / (dist * 5280)) * 100

    const gainPenalty  = 0.04 * gainGrade
    const lossBenefit  = Math.min(0.02 * lossGrade, 0.12)

    return Math.max(0.80, Math.min(1.40, 1.0 + gainPenalty - lossBenefit + fatiguePenalty))
  }

  // Fallback when no elevation data is available
  const DIFFICULTY_FALLBACK: Record<string, number> = {
    E:  0.95,
    M:  1.00,
    MC: 1.12,
    MD: 1.25,
  }
  return Math.max(0.80, Math.min(1.40, (DIFFICULTY_FALLBACK[difficultyRating ?? ''] ?? 1.0) + fatiguePenalty))
}

export function formatPace(paceMinPerMile: number | null | undefined): string {
  if (paceMinPerMile === null || paceMinPerMile === undefined || paceMinPerMile === 0) {
    return '—'
  }
  const minutes = Math.floor(paceMinPerMile)
  const seconds = Math.round((paceMinPerMile - minutes) * 60)
  return `${minutes}:${seconds.toString().padStart(2, '0')}`
}
